/**
 * Notifications view (task 21.2): notification preference + delivery log.
 *
 * - **Preference** — an auto-saving settings toggle (and optional channel).
 *   The stored preference is read on mount via `GET /notifications/preference`
 *   (the service returns the `enabled: false` default when none is stored) so
 *   the switch always reflects the true saved state. Flipping the switch or
 *   changing the channel immediately persists via `PUT /notifications/preference`
 *   with an optimistic update that reverts on failure; the change applies to
 *   transactions posted more than 5s later (R7.7).
 * - **Log** — `GET /notifications` lists delivery entries including retained
 *   failures with their last error and attempt count (R7.5).
 *
 * Envelope errors from any call render inline via {@link ErrorNotice}.
 */

import { useEffect, useId, useState } from 'react';

import { notificationsApi } from '../api/index.js';
import { ErrorNotice } from '../ui/ErrorNotice.js';
import { formatDate } from '../ui/format.js';
import { useAction, useAsyncData } from '../ui/useAsync.js';

/** How long the transient "Saved" status stays visible. */
const SAVED_STATUS_MS = 2500;

/**
 * The enable/disable + channel preference toggle (R7.7).
 *
 * Loads the current preference on mount and syncs the controls once it
 * arrives. Every change to the switch or the channel select auto-saves:
 * the UI updates optimistically, adopts the server-returned value on success
 * (with a transient "Saved" status), and reverts to the previous value on
 * failure while surfacing the error envelope. Controls are disabled while the
 * initial load or a save is in flight so a save can never be double-submitted.
 * If the initial load fails the error is shown but the controls remain usable
 * with the default (off) state so the customer can still set a preference.
 */
function PreferenceForm(): JSX.Element {
  const [enabled, setEnabled] = useState(false);
  const [channel, setChannel] = useState('sms');
  const [status, setStatus] = useState<string | undefined>(undefined);
  const enabledId = useId();
  const channelId = useId();

  const current = useAsyncData(() => notificationsApi.getNotificationPreference(), []);

  // Adopt the stored preference as soon as it arrives.
  useEffect(() => {
    if (current.data !== undefined) {
      setEnabled(current.data.enabled);
      setChannel(current.data.channel ?? 'sms');
    }
  }, [current.data]);

  // Clear the transient "Saved" status after a short delay.
  useEffect(() => {
    if (status === undefined) {
      return undefined;
    }
    const timer = window.setTimeout(() => setStatus(undefined), SAVED_STATUS_MS);
    return () => window.clearTimeout(timer);
  }, [status]);

  const save = useAction((nextEnabled: boolean, nextChannel: string) =>
    notificationsApi.setNotificationPreference(
      nextEnabled,
      // Always persist a concrete channel: a null channel makes the worker
      // suppress delivery (R7.6), which reads as "SMS stopped working".
      nextChannel.trim().length > 0 ? nextChannel.trim() : 'sms',
    ),
  );

  /**
   * Optimistically apply `next*` to the controls, persist, then either adopt the
   * server's stored value or roll back to the previous state on failure.
   */
  async function persist(nextEnabled: boolean, nextChannel: string): Promise<void> {
    const previousEnabled = enabled;
    const previousChannel = channel;
    setStatus(undefined);
    setEnabled(nextEnabled);
    setChannel(nextChannel);

    const result = await save.run(nextEnabled, nextChannel);
    if (result !== undefined) {
      setEnabled(result.enabled);
      setChannel(result.channel ?? 'sms');
      setStatus('Saved');
    } else {
      setEnabled(previousEnabled);
      setChannel(previousChannel);
    }
  }

  const busy = current.loading || save.pending;

  return (
    <section aria-labelledby="pref-heading" className="esb-card">
      <h3 id="pref-heading" className="esb-subhead">Notification preference</h3>

      <ErrorNotice error={current.error} />
      <ErrorNotice error={save.error} />
      <p role="status" className="esb-status">
        {current.loading ? 'Loading preference…' : save.pending ? 'Saving…' : status ?? ''}
      </p>

      <div className="esb-toggle-row">
        <label htmlFor={enabledId} className="esb-field__label">Enable transaction notifications</label>
        <span className="esb-toggle">
          <input
            id={enabledId}
            type="checkbox"
            className="esb-toggle__input"
            checked={enabled}
            disabled={busy}
            onChange={(e) => void persist(e.target.checked, channel)}
          />
          <span className="esb-toggle__track" aria-hidden="true">
            <span className="esb-toggle__thumb" />
          </span>
        </span>
      </div>

      <div className="esb-field">
        <label htmlFor={channelId} className="esb-field__label">Channel</label>
        <select
          id={channelId}
          className="esb-select"
          value={channel}
          onChange={(e) => void persist(enabled, e.target.value)}
          disabled={busy || !enabled}
        >
          <option value="email">Email</option>
          <option value="sms">SMS</option>
        </select>
      </div>
    </section>
  );
}

/** The notification delivery log incl. retained failures (R7.5). */
function NotificationLog(): JSX.Element {
  const log = useAsyncData(() => notificationsApi.getNotificationLog(), []);
  const entries = log.data?.notifications ?? [];

  return (
    <section aria-labelledby="log-heading" className="esb-card">
      <h3 id="log-heading" className="esb-subhead">Notification log</h3>

      <ErrorNotice error={log.error} />
      <div aria-live="polite">{log.loading ? <p className="esb-loading">Loading log…</p> : null}</div>

      {!log.loading && entries.length === 0 && log.error === undefined ? (
        <p className="esb-empty">No notifications yet.</p>
      ) : entries.length > 0 ? (
        <ul className="esb-log" role="list" aria-label="Notification delivery log">
          {entries.map((entry) => (
            <li key={entry.notificationId} className="esb-log__row" role="listitem">
              <span className="esb-log__body">
                <span className="esb-log__detail">{entry.lastError ?? entry.content}</span>
                <span className="esb-log__date">{formatDate(entry.createdAt)}</span>
              </span>
              <span className="esb-log__right">
                <span className={`esb-badge esb-badge--${entry.status.toLowerCase()}`}>
                  {entry.status}
                </span>
                <span className="esb-log__attempts">
                  {entry.attempts} {entry.attempts === 1 ? 'attempt' : 'attempts'}
                </span>
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

/** Notifications panel: auto-saving preference toggle + delivery log. */
export function NotificationsView(): JSX.Element {
  return (
    <section aria-labelledby="notifications-heading">
      <h2 id="notifications-heading" className="esb-section-title">Notifications</h2>
      <PreferenceForm />
      <NotificationLog />
    </section>
  );
}
